import React from 'react';
import PropTypes from 'prop-types';
import { Controller, useForm, useFormState } from 'react-hook-form';
import CustomFormInput from '../forms/CustomFormInput';
import { Col, Form, FormLabel } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import * as yup from "yup";
import { yupResolver } from '@hookform/resolvers/yup';
import ReadonlyInput from '../forms/ReadonlyInput';
import Select from 'react-select';
import CustomFormSelect from '../forms/CustomFormSelect';
import CustomFormSwitch from '../forms/CustomFormSwitch';
import { SYSTEM_UNION_CODE } from '../../config';

const AccountForm = ({ defaultValues, onSubmit, onError }) => {
  const { t } = useTranslation();
  const unionModeEnabled = Boolean(SYSTEM_UNION_CODE);
  const nameReadOnly = Boolean(defaultValues?.nameProvided);
  const emailReadOnly = Boolean(defaultValues?.emailProvided);

  const schema = React.useMemo(() => yup.object({
    name: yup.string().when('$nameReadOnly', {
      is: true,
      then: (schema) => schema.nullable().notRequired(),
      otherwise: (schema) => schema.required().min(3)
    }),
    phone: yup.string().notRequired(),
    email: yup.string().when('$emailReadOnly', {
      is: true,
      then: (schema) => schema.nullable().notRequired(),
      otherwise: (schema) => schema.required().email()
    }),
    roles: yup.array().required().min(1)
  }).required(), []);

  const { register, formState: { errors, isDirty }, handleSubmit, control } = useForm({
    mode: "onChange",
    context: { emailReadOnly, nameReadOnly },
    defaultValues: defaultValues,
    resolver: yupResolver(schema)
  });

  const { isValid, dirtyFields, isSubmitting } = useFormState({
    control
  });


  const hiddenRoleValues = React.useMemo(
    () => (unionModeEnabled ? [] : ['UNION_ADMIN', 'UNION_MODE']),
    [unionModeEnabled]
  );

  const filterVisibleRoles = React.useCallback(
    (roles = []) => roles.filter((role) => !hiddenRoleValues.includes(role)),
    [hiddenRoleValues]
  );

  const extractHiddenRoles = React.useCallback(
    (roles = []) => roles.filter((role) => hiddenRoleValues.includes(role)),
    [hiddenRoleValues]
  );

  const roleOptions = React.useMemo(() => (
    [
      { label: "UNION_ADMIN", value: "UNION_ADMIN" },
      { label: "ADMIN", value: "ADMIN" },
      { label: "USER", value: "USER" }
    ].filter((option) => !hiddenRoleValues.includes(option.value))
  ), [hiddenRoleValues]);
  const statusOptions = [
    { id: 'ACTIVE', value: 'Active' },
    { id: 'DISABLED', value: 'Disabled' }
  ]
  const formatRoles = (roles = []) => filterVisibleRoles(roles)
    .map((role) => t('role:' + role))
    .join(', ');
  const formatStatus = (status) => (status ? t('status:' + status) : '');

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="row">

        <div className="col-xs-12 mb-3">

          <FormLabel className="fw-bold" htmlFor="name">{t('account:lblUsername')}</FormLabel>
          <ReadonlyInput value={defaultValues.username} />
        </div>

        <div className="col-xs-12 col-md-6 mb-3">

          <FormLabel className="fw-bold" htmlFor="name">{t('account:lblName')}<span
            className="requiredIndicator">*</span> </FormLabel>
          <CustomFormInput fieldName='name'
            params={{ required: true }}
            register={register}
            readOnly={defaultValues.nameProvided}
            dirtyFields={dirtyFields}
            errorMessage={errors?.name?.message} />
        </div>


        <div className="col-xs-12 col-md-6 mb-3">

          <FormLabel className="fw-bold" htmlFor="email">{t('account:lblEmail')}<span
            className="requiredIndicator">*</span> </FormLabel>
          <CustomFormInput fieldName='email'
            params={{ required: true }}
            readOnly={defaultValues.emailProvided}
            register={register}
            dirtyFields={dirtyFields}
            errorMessage={errors?.email?.message} />
        </div>
      </div>
      <div className="row">

        <div className="col-xs-12 col-md-6 mb-3">

          <FormLabel className="fw-bold" htmlFor="phone">{t('account:lblPhone')}</FormLabel>
          <CustomFormInput fieldName='phone'
            params={{ required: false }}
            readOnly={defaultValues.phoneProvided || unionModeEnabled}
            register={register}
            dirtyFields={dirtyFields}
            errorMessage={errors?.phone?.message} />
        </div>
      </div>

      <div className="row">

        <div className="col-xs-12 col-md-6 mb-3">

          <FormLabel className="fw-bold" htmlFor="roles">{t('account:lblRoles')}<span
            className="requiredIndicator">*</span> </FormLabel>
          {unionModeEnabled ? (
            <Controller
              control={control}
              defaultValue={defaultValues.roles}
              name="roles"
              render={({ field: { value } }) => (
                <ReadonlyInput value={formatRoles(value)} />
              )}
            />
          ) : (
            <>
              <Controller
                control={control}
                defaultValue={defaultValues.roles}
                name="roles"
                render={({ field: { onChange, value, ref } }) => (
                  <Select
                    inputRef={ref}
                    isMulti
                    isClearable
                    classNamePrefix="addl-class"
                    options={roleOptions}
                    value={roleOptions.filter((option) =>
                      filterVisibleRoles(value || []).includes(option.value)
                    )}
                    onChange={(val) => {
                      const selectedRoles = val ? val.map((vals) => vals.value) : [];
                      const hiddenRoles = extractHiddenRoles(value || []);
                      onChange([...hiddenRoles, ...selectedRoles])
                    }}
                  />
                )}
              />
              {errors.roles && <p role="alert" className='text-danger'>{errors.roles.message}</p>}
            </>
          )}

        </div>
        <div className="col-xs-12 col-md-6 mb-3">

          <FormLabel className="fw-bold" htmlFor="status">{t('account:lblStatus')}<span
            className="requiredIndicator">*</span> </FormLabel>

          {unionModeEnabled ? (
            <Controller
              control={control}
              defaultValue={defaultValues.status}
              name="status"
              render={({ field: { value } }) => (
                <ReadonlyInput value={formatStatus(value)} />
              )}
            />
          ) : (
            <CustomFormSelect fieldName='status'
              skipNull
              params={{ required: true }}
              register={register}
              dirtyFields={dirtyFields}
              options={statusOptions}
              errorMessage={errors?.status?.message} />
          )}
        </div>
      </div>

      <div className="row">

        <div className="col-xs-12 col-md-6 mb-3">
        <FormLabel className="fw-bold" htmlFor="canPerformOperations">{t('account:lblCanPerformOperations')}</FormLabel>
        <CustomFormSwitch fieldName='canPerformOperations'
            register={register}
            dirtyFields={dirtyFields}
            errorMessage={errors?.canPerformOperations?.message} />
        </div>

        <div className="col-xs-12 col-md-6 mb-3">
          <FormLabel className="fw-bold" htmlFor="canChangeOwnOffice">{t('account:lblCanChangeOwnOffice')}</FormLabel>
          <CustomFormSwitch fieldName='canChangeOwnOffice'
                            register={register}
                            dirtyFields={dirtyFields}
                            errorMessage={errors?.canChangeOwnOffice?.message} />
        </div>
      </div>
      <div className="row">

        <div className="col-xs-12 col-md-6 mb-3">
          <FormLabel className="fw-bold" htmlFor="canManageSysOptionsAndUsers">
            {t('account:lblCanManageSysOptionsAndUsers')}
          </FormLabel>
          <CustomFormSwitch fieldName='canManageSysOptionsAndUsers'
            register={register}
            dirtyFields={dirtyFields}
            errorMessage={errors?.canManageSysOptionsAndUsers?.message} />
        </div>

        <div className="col-xs-12 col-md-6 mb-3">
          <FormLabel className="fw-bold" htmlFor="canManageSettings">{t('account:lblCanManageSettings')}</FormLabel>
          <CustomFormSwitch fieldName='canManageSettings'
                            register={register}
                            dirtyFields={dirtyFields}
                            errorMessage={errors?.canManageSettings?.message} />
        </div>
      </div>
      <Form.Group className="row">
        <Col>
          <button type="submit" id="btnSaveUser" color="primary" className="btn btn-success  mt-3 me-2"
            disabled={!isValid || isSubmitting || !isDirty}>
            {t('common:btnSubmit')}
          </button>
        </Col>
      </Form.Group>
    </form>
  );
};

AccountForm.propTypes = {
  defaultValues: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
  // onError: PropTypes.func.isRequired,
};

export default AccountForm;
